// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTableRowExpansion.tsx
 * @input React, StyleX, Icon, Table types
 * @output Exports useTableRowExpansion hook + config/state types
 * @position Row-expansion plugin; consumed by Table via plugins prop
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/index.ts (exports)
 */
import { useCallback, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../../../theme/tokens.stylex.js";
import { spacingVars, colorVars, radiusVars } from "../../../theme/tokens.stylex.js";
import { Icon } from "../../../Icon/index.js";
import { resolveContextActions } from "../../tableContextMenu.js";
import { useTranslator } from "../../../i18n/index.js";

// =============================================================================
// Config
// =============================================================================

/**
 * Configuration for useTableRowExpansion (inherited-columns mode).
 *
 * Child rows use the same columns as their parents, with indentation on the
 * first content column. The consumer provides a **flat** data array (use
 * {@link useTableRowExpansionState} to flatten a tree) and a `getDepth`
 * function so the plugin knows each row's nesting level.
 */

/**
 * Configuration for {@link useTableRowExpansionState}.
 *
 * Mirrors the shape of {@link useTableSelectionState}: you own the
 * `expandedKeys` set (via `useState`), and the hook derives everything the
 * plugin needs — the flattened `data`, per-row depth, expand/collapse
 * handlers, and the expand-all toggle state.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Manages row-expansion state and derives the config for
 * {@link useTableRowExpansion}. This is the recommended entry point — it
 * removes the boilerplate of flattening the tree, tracking depth, and
 * computing the expand-all state.
 *
 * @example
 * ```
 * const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
 * const {data, expansionConfig} = useTableRowExpansionState({
 *   baseData: tree,
 *   getChildren: item => item.children ?? [],
 *   getRowKey: item => item.id,
 *   expandedKeys,
 *   setExpandedKeys,
 * });
 * const expansion = useTableRowExpansion(expansionConfig);
 * <Table data={data} columns={columns} idKey="id" plugins={{expansion}} />;
 * ```
 */
export function useTableRowExpansionState({
  baseData,
  getChildren,
  getRowKey,
  getIsItemExpandable,
  expandedKeys,
  setExpandedKeys
}) {
  const isExpandable = useCallback(item => getIsItemExpandable ? getIsItemExpandable(item) : getChildren(item).length > 0, [getIsItemExpandable, getChildren]);
  const depthMap = useMemo(() => {
    const map = new Map();
    function walk(items, depth) {
      for (const item of items) {
        const key = getRowKey(item);
        map.set(key, depth);
        if (expandedKeys.has(key)) {
          walk(getChildren(item), depth + 1);
        }
      }
    }
    walk(baseData, 0);
    return map;
  }, [baseData, getChildren, getRowKey, expandedKeys]);
  const data = useMemo(() => {
    const result = [];
    function walk(items) {
      for (const item of items) {
        result.push(item);
        const key = getRowKey(item);
        if (expandedKeys.has(key)) {
          walk(getChildren(item));
        }
      }
    }
    walk(baseData);
    return result;
  }, [baseData, getChildren, getRowKey, expandedKeys]);

  // Every expandable key across the whole tree (drives expand-all).
  const allExpandableKeys = useMemo(() => {
    const keys = [];
    function walk(items) {
      for (const item of items) {
        if (isExpandable(item)) {
          keys.push(getRowKey(item));
          walk(getChildren(item));
        }
      }
    }
    walk(baseData);
    return keys;
  }, [baseData, getChildren, getRowKey, isExpandable]);
  const getDepth = useCallback(item => depthMap.get(getRowKey(item)) ?? 0, [depthMap, getRowKey]);
  const onToggle = useCallback(key => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, [setExpandedKeys]);
  const isAllExpanded = useMemo(() => {
    if (allExpandableKeys.length === 0) {
      return false;
    }
    const expandedCount = allExpandableKeys.filter(k => expandedKeys.has(k)).length;
    if (expandedCount === 0) {
      return false;
    }
    if (expandedCount === allExpandableKeys.length) {
      return true;
    }
    return 'indeterminate';
  }, [allExpandableKeys, expandedKeys]);
  const onToggleExpandAll = useCallback(expand => {
    setExpandedKeys(expand ? new Set(allExpandableKeys) : new Set());
  }, [setExpandedKeys, allExpandableKeys]);
  const expansionConfig = useMemo(() => ({
    expandedKeys,
    onToggle,
    getRowKey,
    getChildren,
    getDepth,
    getIsItemExpandable,
    isAllExpanded,
    onToggleExpandAll
  }), [expandedKeys, onToggle, getRowKey, getChildren, getDepth, getIsItemExpandable, isAllExpanded, onToggleExpandAll]);
  return {
    data,
    expansionConfig
  };
}

// =============================================================================
// Styles
// =============================================================================

const EXPANSION_COLUMN_WIDTH = {
  type: 'pixel',
  value: 40
};

/** Indentation applied per depth level, in pixels. */
const INDENT_PER_DEPTH = 24;
const _temp = {
  kZCmMZ: "xnvo3vl",
  "$$css": true
};
const expansionStyles = {
  indentedCell: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    $$css: true
  },
  indent: px => [_temp, {
    "--x-paddingInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${px}px`)
  }],
  clickableRow: {
    kkrTdU: "x1ypdohk",
    $$css: true
  }
};

// =============================================================================
// Chevron
// =============================================================================

function ExpansionChevron({
  isExpanded,
  onToggle,
  ariaLabel
}) {
  return /*#__PURE__*/_jsx("button", {
    type: "button",
    ...{
      className: "x3nfvp2 x6s0dn4 xl56j7k xvy4d1p xxk0z11 xx3sua9 x1ypdohk xv9yike xefglzl xx6bhzk x1717udv x2lah0s x1ilzqfv x1cqbx0l"
    },
    onClick: e => {
      e.stopPropagation();
      onToggle();
    },
    "aria-label": ariaLabel,
    "aria-expanded": isExpanded,
    children: /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x3nfvp2 x11xpdln xx6bhzk"
        },
        1: {
          className: "x3nfvp2 x11xpdln xx6bhzk x1iffjtl"
        }
      }[!!isExpanded << 0],
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "chevronRight",
        size: "xsm"
      })
    })
  });
}

// =============================================================================
// Hook
// =============================================================================

export function useTableRowExpansion(config) {
  const t = useTranslator();
  const {
    expandedKeys,
    onToggle,
    getRowKey,
    getChildren,
    getDepth,
    getIsItemExpandable,
    hasRowClickExpansion = false,
    isAllExpanded,
    onToggleExpandAll
  } = config;

  // Track the first non-plugin column key for indentation.
  const firstUserColumnKeyRef = useRef(null);
  const expansionColumn = useMemo(() => ({
    key: '__expansion',
    header: '',
    width: EXPANSION_COLUMN_WIDTH,
    resizable: false,
    renderCell: item => {
      // Child rows (depth > 0) show their chevron inline in the first user
      // column instead — don't double up here.
      const depth = getDepth ? getDepth(item) : 0;
      if (depth > 0) {
        return null;
      }
      const key = getRowKey(item);
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : getChildren(item).length > 0;
      if (!expandable) {
        return null;
      }
      const isExpanded = expandedKeys.has(key);
      return /*#__PURE__*/_jsx(ExpansionChevron, {
        isExpanded: isExpanded,
        onToggle: () => onToggle(key),
        ariaLabel: isExpanded ? t('@astryx.tableRowExpansion.collapseRow') : t('@astryx.tableRowExpansion.expandRow')
      });
    }
  }), [expandedKeys, onToggle, getRowKey, getChildren, getIsItemExpandable, getDepth, t]);
  return useMemo(() => ({
    transformColumns(columns) {
      // Track the first user column for indentation.
      const firstUserCol = columns.find(c => !c.key.startsWith('__'));
      firstUserColumnKeyRef.current = firstUserCol?.key ?? null;

      // Wrap the first user column's renderCell to add depth indentation +
      // an inline chevron for child rows. This is the inherited-columns
      // pattern: child rows use the same columns but indent their first
      // content cell.
      const wrappedColumns = columns.map(col => {
        if (col.key !== firstUserColumnKeyRef.current) {
          return col;
        }
        const originalRenderCell = col.renderCell;
        return {
          ...col,
          renderCell: item => {
            const depth = getDepth ? getDepth(item) : 0;
            const originalContent = originalRenderCell ? originalRenderCell(item) : String(item[col.key] ?? '');
            if (depth === 0) {
              return originalContent;
            }
            const indent = (depth - 1) * INDENT_PER_DEPTH;
            const key = getRowKey(item);
            const isExpanded = expandedKeys.has(key);
            const expandable = getIsItemExpandable ? getIsItemExpandable(item) : getChildren(item).length > 0;
            const chevron = expandable ? /*#__PURE__*/_jsx(ExpansionChevron, {
              isExpanded: isExpanded,
              onToggle: () => onToggle(key),
              ariaLabel: isExpanded ? t('@astryx.tableRowExpansion.collapseRow') : t('@astryx.tableRowExpansion.expandRow')
            }) : /*#__PURE__*/_jsx("span", {
              ...{
                className: "x1rg5ohu xvy4d1p xxk0z11 x2lah0s"
              }
            });
            return /*#__PURE__*/_jsxs("div", {
              ...stylex.props(expansionStyles.indentedCell, indent > 0 && expansionStyles.indent(indent)),
              children: [chevron, originalContent]
            });
          }
        };
      });
      return [expansionColumn, ...wrappedColumns];
    },
    transformHeaderCell(props, column) {
      if (column.key !== '__expansion') {
        return props;
      }

      // Show expand-all toggle when the consumer provides the state + callback.
      if (isAllExpanded !== undefined && onToggleExpandAll) {
        const allExpanded = isAllExpanded === true;
        return {
          ...props,
          content: /*#__PURE__*/_jsx("button", {
            type: "button",
            ...{
              className: "x3nfvp2 x6s0dn4 xl56j7k xvy4d1p xxk0z11 xx3sua9 x1ypdohk xv9yike xefglzl xx6bhzk x1717udv x2lah0s x1ilzqfv x1cqbx0l"
            },
            onClick: () => onToggleExpandAll(!allExpanded),
            "aria-label": allExpanded ? t('@astryx.tableRowExpansion.collapseAllRows') : t('@astryx.tableRowExpansion.expandAllRows'),
            children: /*#__PURE__*/_jsx("span", {
              ...{
                0: {
                  className: "x3nfvp2 x11xpdln xx6bhzk"
                },
                1: {
                  className: "x3nfvp2 x11xpdln xx6bhzk x1iffjtl"
                }
              }[!!allExpanded << 0],
              children: /*#__PURE__*/_jsx(Icon, {
                icon: "chevronRight",
                size: "xsm"
              })
            })
          })
        };
      }
      return {
        ...props,
        content: null
      };
    },
    transformBodyCell(props, column, item) {
      // Contribute "Expand/Collapse row" context-menu action on every cell
      // so right-clicking anywhere in the row shows the option.
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : getChildren(item).length > 0;
      if (!expandable) {
        return props;
      }
      const key = getRowKey(item);
      const isExpanded = expandedKeys.has(key);
      return {
        ...props,
        contextMenuActions: () => [...resolveContextActions(props.contextMenuActions), {
          id: 'row-expansion-toggle',
          group: 'row-expansion',
          label: isExpanded ? 'Collapse row' : 'Expand row',
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: isExpanded ? 'chevronDown' : 'chevronRight',
            size: "xsm",
            "aria-hidden": true
          }),
          onSelect: () => onToggle(key)
        }]
      };
    },
    transformBodyRow(props, item) {
      if (!hasRowClickExpansion) {
        return props;
      }
      const expandable = getIsItemExpandable ? getIsItemExpandable(item) : getChildren(item).length > 0;
      if (!expandable) {
        return props;
      }
      const key = getRowKey(item);
      return {
        ...props,
        htmlProps: {
          ...props.htmlProps,
          onClick: () => onToggle(key)
        },
        xstyle: [...props.xstyle, expansionStyles.clickableRow]
      };
    }
  }), [expandedKeys, getRowKey, onToggle, getChildren, getDepth, getIsItemExpandable, hasRowClickExpansion, isAllExpanded, onToggleExpandAll, expansionColumn, t]);
}